import React from "react";
import { useRouter } from "next/router";

export interface ModuleItem {
  id: string;
  title: string;
  href: string;
  description: string;
  badge?: string;
  badgeVariant?: "green" | "blue" | "amber" | "neutral";
  icon: React.ReactNode;
}

interface ModuleCardProps {
  module: ModuleItem;
}

/**
 * Card compacta de módulo para el Dashboard principal.
 * Sustituye las tarjetas gigantes anteriores. Toda la card es clickeable con hover y focus accesible.
 */
export const ModuleCard: React.FC<ModuleCardProps> = ({ module }) => {
  const router = useRouter();

  const getBadgeStyle = () => {
    switch (module.badgeVariant) {
      case "green":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "amber":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "blue":
        return "bg-blue-50 text-blue-700 border-blue-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  const handleClick = () => {
    if (module.href.includes("/programas/resultados/")) {
      window.location.href = module.href;
    } else {
      router.push(module.href);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className="group relative bg-white rounded-2xl p-5 border border-gray-200/80 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer flex flex-col justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
    >
      <div>
        {/* Top: Icon & Badge */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200 shrink-0">
            {module.icon}
          </div>

          {module.badge && (
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getBadgeStyle()}`}>
              {module.badge}
            </span>
          )}
        </div>

        {/* Title & Description */}
        <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">
          {module.title}
        </h3>
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
          {module.description}
        </p>
      </div>

      {/* Footer link arrow */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-medium text-gray-400 group-hover:text-blue-600 transition-colors">
        <span>Acceder al módulo</span>
        <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14" />
          <path d="M12 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
};

export default ModuleCard;
